/**
 * Jint JavaScript Runtime Validation Patterns
 *
 * These patterns align with the blocked globals and constraints in:
 * Cassie.ConnectorService/ConnectorService.Worker/Services/Transformation/JavaScriptTransformer.cs
 *
 * Pattern matching uses word boundaries (\b) to prevent false positives
 * (e.g., "import" in "important" should not trigger)
 */

// Monaco MarkerSeverity values
export const MarkerSeverity = {
	Hint: 1,
	Info: 2,
	Warning: 4,
	Error: 8
}

/**
 * Blocked patterns - these will cause runtime errors in Jint
 * Aligned with JavaScriptTransformer.cs DetectBlockedPatterns() method
 */
export const BLOCKED_PATTERNS = [
	// Code Execution (lines 400-403 in JavaScriptTransformer.cs)
	{
		pattern: /\beval\s*\(/g,
		keyword: 'eval',
		message: 'eval() is not allowed - dynamic code execution is blocked for security',
		category: 'code-execution'
	},
	{
		pattern: /\bFunction\s*\(/g,
		keyword: 'Function',
		message: 'Function() constructor is not allowed - use function declarations instead',
		category: 'code-execution'
	},
	{
		pattern: /\bnew\s+Function\b/g,
		keyword: 'new Function',
		message: 'new Function() is not allowed - use function declarations instead',
		category: 'code-execution'
	},
	{
		pattern: /\bGeneratorFunction\b/g,
		keyword: 'GeneratorFunction',
		message: 'Generator functions are not supported',
		category: 'code-execution'
	},
	{
		pattern: /\basync\s+function\b/g,
		keyword: 'async function',
		message: 'Async functions are not supported - transformations runs synchronously only',
		category: 'async'
	},

	// Module System (lines 406-408)
	{
		pattern: /\brequire\s*\(/g,
		keyword: 'require',
		message: 'require() is not available - all transformations run in an isolated sandbox',
		category: 'module-system'
	},
	{
		pattern: /\bimport\s+/g,
		keyword: 'import',
		message: 'ES6 import statements are not supported - use ES5 syntax only',
		category: 'module-system'
	},
	{
		pattern: /\bimportScripts\s*\(/g,
		keyword: 'importScripts',
		message: 'importScripts() is not available',
		category: 'module-system'
	},
	{
		pattern: /\bexport\s+/g,
		keyword: 'export',
		message: 'ES6 export statements are not supported',
		category: 'module-system'
	},

	// Network Operations (lines 409-417)
	{
		pattern: /\bfetch\s*\(/g,
		keyword: 'fetch',
		message: 'Network operations are not allowed - fetch() is blocked',
		category: 'network'
	},
	{
		pattern: /\bXMLHttpRequest\b/g,
		keyword: 'XMLHttpRequest',
		message: 'Network operations are not allowed - XMLHttpRequest is blocked',
		category: 'network'
	},
	{
		pattern: /\bActiveXObject\b/g,
		keyword: 'ActiveXObject',
		message: 'ActiveXObject is not available',
		category: 'network'
	},
	{
		pattern: /\bWebSocket\b/g,
		keyword: 'WebSocket',
		message: 'WebSocket connections are not allowed in transformations',
		category: 'network'
	},
	{
		// Use constructor pattern to avoid false positives on variable names like "coworker"
		pattern: /\bnew\s+Worker\s*\(/g,
		keyword: 'Worker',
		message: 'Web Workers are not available',
		category: 'network'
	},
	{
		pattern: /\bnew\s+SharedWorker\s*\(/g,
		keyword: 'SharedWorker',
		message: 'SharedWorker is not available',
		category: 'network'
	},
	{
		pattern: /\bnew\s+ServiceWorker\s*\(|\bServiceWorker\s*\./g,
		keyword: 'ServiceWorker',
		message: 'ServiceWorker is not available',
		category: 'network'
	},

	// File System (lines 418-420)
	// Use constructor/call patterns to avoid false positives on variable names
	{
		pattern: /\bnew\s+FileReader\b|\bFileReader\s*\(/g,
		keyword: 'FileReader',
		message: 'File system access is not available in the sandbox',
		category: 'file-system'
	},
	{
		pattern: /\bnew\s+File\s*\(|\bFile\s*\(/g,
		keyword: 'File',
		message: 'File objects are not available in the sandbox',
		category: 'file-system'
	},
	{
		pattern: /\bnew\s+Blob\s*\(|\bBlob\s*\(/g,
		keyword: 'Blob',
		message: 'Blob API is not available in the sandbox',
		category: 'file-system'
	},
	{
		pattern: /\bFileSystem\b/g,
		keyword: 'FileSystem',
		message: 'FileSystem API is not available',
		category: 'file-system'
	},

	// Timing Functions (lines 423-430)
	{
		pattern: /\bsetTimeout\s*\(/g,
		keyword: 'setTimeout',
		message: 'setTimeout() is not available - transformations must execute synchronously',
		category: 'timing'
	},
	{
		pattern: /\bsetInterval\s*\(/g,
		keyword: 'setInterval',
		message: 'setInterval() is not available - transformations must execute synchronously',
		category: 'timing'
	},
	{
		pattern: /\bsetImmediate\s*\(/g,
		keyword: 'setImmediate',
		message: 'setImmediate() is not available',
		category: 'timing'
	},
	{
		pattern: /\bclearTimeout\b/g,
		keyword: 'clearTimeout',
		message: 'clearTimeout() is not available',
		category: 'timing'
	},
	{
		pattern: /\bclearInterval\b/g,
		keyword: 'clearInterval',
		message: 'clearInterval() is not available',
		category: 'timing'
	},
	{
		pattern: /\bclearImmediate\b/g,
		keyword: 'clearImmediate',
		message: 'clearImmediate() is not available',
		category: 'timing'
	},
	{
		pattern: /\brequestAnimationFrame\s*\(/g,
		keyword: 'requestAnimationFrame',
		message: 'requestAnimationFrame() is not available',
		category: 'timing'
	},
	{
		pattern: /\bcancelAnimationFrame\b/g,
		keyword: 'cancelAnimationFrame',
		message: 'cancelAnimationFrame() is not available',
		category: 'timing'
	},

	// Debugging (lines 433-434)
	{
		pattern: /\bconsole\s*\./g,
		keyword: 'console',
		message: 'console object is not available - transformations execute server-side in isolation',
		category: 'debugging'
	},
	{
		pattern: /\bdebugger\b/g,
		keyword: 'debugger',
		message: 'debugger statement is not available - transformations execute server-side',
		category: 'debugging'
	},

	// Global Scope (lines 437-440)
	{
		pattern: /\bwindow\b/g,
		keyword: 'window',
		message: 'window object is not available - this is server-side JavaScript',
		category: 'global-scope'
	},
	{
		pattern: /\bdocument\b/g,
		keyword: 'document',
		message: 'document object is not available - this is server-side JavaScript',
		category: 'global-scope'
	},
	{
		pattern: /\bglobal\b/g,
		keyword: 'global',
		message: 'global object is not available',
		category: 'global-scope'
	},
	{
		pattern: /\bglobalThis\b/g,
		keyword: 'globalThis',
		message: 'globalThis is not available',
		category: 'global-scope'
	},
	{
		pattern: /\bself\b/g,
		keyword: 'self',
		message: 'self object is not available',
		category: 'global-scope'
	},
	{
		pattern: /\bprocess\b/g,
		keyword: 'process',
		message: 'process object is not available - Node.js APIs are not supported',
		category: 'global-scope'
	},
	{
		pattern: /\blocation\b/g,
		keyword: 'location',
		message: 'location object is not available',
		category: 'dom'
	},
	{
		pattern: /\bnavigator\b/g,
		keyword: 'navigator',
		message: 'navigator object is not available',
		category: 'dom'
	},

	// Async Features
	{
		pattern: /\bawait\s+/g,
		keyword: 'await',
		message: 'await keyword is not supported - transformations run synchronously only',
		category: 'async'
	},
	{
		pattern: /\bPromise\b/g,
		keyword: 'Promise',
		message: 'Promise API is not supported - use synchronous code only',
		category: 'async'
	}
]

/**
 * ES6+ features that should trigger warnings with suggestions
 * Jint 4.4.1 targets ES5.1
 */
export const ES6_FEATURES = [
	{
		pattern: /\bconst\s+/g,
		keyword: 'const',
		message: 'const is ES6 - use var for compatibility',
		suggestion: 'Replace "const" with "var"'
	},
	{
		pattern: /\blet\s+/g,
		keyword: 'let',
		message: 'let is ES6 - use var for compatibility',
		suggestion: 'Replace "let" with "var"'
	},
	{
		// Simplified pattern to avoid ReDoS - just detect backtick usage
		pattern: /`[^`]{0,500}`/g,
		keyword: 'template literal',
		message: 'Template literals are ES6 - use string concatenation',
		suggestion: 'Use "text " + variable + " more text" instead'
	},
	{
		pattern: /(?:const|let|var)\s*\{[^}]+\}\s*=/g,
		keyword: 'destructuring',
		message: 'Object destructuring is ES6 - access properties individually',
		suggestion: 'Use var x = obj.x; var y = obj.y; instead'
	},
	{
		pattern: /(?:const|let|var)\s*\[[^\]]+\]\s*=/g,
		keyword: 'array destructuring',
		message: 'Array destructuring is ES6 - access elements individually',
		suggestion: 'Use var first = arr[0]; var second = arr[1]; instead'
	},
	{
		pattern: /\.\.\./g,
		keyword: 'spread operator',
		message: 'Spread operator is ES6 - use Array.prototype methods',
		suggestion: 'Use array1.concat(array2) instead of [...array1, ...array2]'
	},
	{
		pattern: /\bclass\s+\w+/g,
		keyword: 'class',
		message: 'ES6 classes have limited support - prefer function constructors',
		suggestion: 'Use function MyClass() {} instead'
	},
	{
		pattern: /\bfor\s*\([^)]*\s+of\s+/g,
		keyword: 'for...of',
		message: 'for...of is ES6 - use traditional for loop or forEach',
		suggestion: 'Use for (var i = 0; i < arr.length; i++) instead'
	},
	{
		pattern: /=>\s*\{/g,
		keyword: 'arrow function',
		message: 'Arrow functions are supported but "this" binding differs - prefer function declarations for complex logic',
		suggestion: 'Use function(x) { return x; } instead'
	}
]

/**
 * Suspicious patterns - allowed but logged for security audit
 * Aligned with JavaScriptTransformer.cs lines 559-587
 */
export const SUSPICIOUS_PATTERNS = [
	{
		pattern: /\bconstructor\b/g,
		keyword: 'constructor',
		message: 'constructor usage detected - ensure this is intentional'
	},
	{
		pattern: /\bprototype\b/g,
		keyword: 'prototype',
		message: 'prototype modification detected - ensure this is intentional'
	},
	{
		pattern: /__proto__/g,
		keyword: '__proto__',
		message: '__proto__ access detected - avoid prototype chain manipulation'
	},
	{
		pattern: /\bwith\s*\(/g,
		keyword: 'with',
		message: 'with() statement is deprecated and not recommended'
	},
	{
		pattern: /\barguments\.callee\b/g,
		keyword: 'arguments.callee',
		message: 'arguments.callee is deprecated - use named functions'
	}
]

/**
 * Jint runtime execution limits
 * Aligned with JavaScriptTransformer.cs lines 21-42
 */
export const JINT_LIMITS = {
	executionTimeout: 5000, // 5 seconds
	maxRecursionDepth: 50,
	maxMemoryBytes: 10 * 1024 * 1024, // 10 MB
	maxOutputSizeBytes: 1 * 1024 * 1024, // 1 MB
	maxLoopIterations: 10000 // For audit logging
}

/**
 * Create a Monaco marker from validation result
 * @param {number} severity - Monaco MarkerSeverity value
 * @param {string} message - Error/warning message
 * @param {number} startLineNumber - Starting line
 * @param {number} startColumn - Starting column
 * @param {number} endLineNumber - Ending line
 * @param {number} endColumn - Ending column
 * @returns {Object} Monaco IMarkerData object
 */
export function createMarker (
	severity,
	message,
	startLineNumber,
	startColumn,
	endLineNumber,
	endColumn
) {
	return {
		severity,
		message,
		startLineNumber,
		startColumn,
		endLineNumber,
		endColumn
	}
}
